import { useState } from "react";
import { Textarea } from "@component/textarea";
import { Label } from "@component/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { RadioGroup, RadioGroupItem } from "@component/radio-group";
import {
    Settings,
    Mic,
    Globe,
    Clock,
    CheckCircle2,
    Circle,
    FileText,
    HandIcon as Wave,
    CheckCircle,
    Info,
    Volume2Icon,
    Loader2,
    Phone,
} from "lucide-react";
import { Switch } from "@component/switch";
import { InterviewAssessment } from "@type/assessments";
import { LANGUAGES } from "@type/languages";
import { useUserProfileContext } from "@context/user_profile";
import { cn } from "@util/tailwind";
import { Tooltip } from "@component/tooltip";
import { renderTextWithLineBreaks } from "@util/format";

interface Props {
    mode: "view" | "edit";
    data: InterviewAssessment;
    onDataChange: (data: InterviewAssessment) => void;
    onValidationChange?: (isValid: boolean) => void;
}

export default function InterviewSettingsSectionV2({
    mode,
    data,
    onDataChange,
    onValidationChange,
}: Props) {
    const [isLoadingUserInfo, currentUserInfo] = useUserProfileContext();
    const [isPlayingAudio, setIsPlayingAudio] = useState(false);
    const [playingIndex, setPlayingIndex] = useState<number | null>(null);
    // Calculate progress checklist
    const checklistItems = [
        {
            label: "Create a brief Interview Description",
            completed: !!data.content.description,
        },
        {
            label: "Select your AI Voice",
            completed: !!data.content.voice
        },
        {
            label: "Select your interview language",
            completed: !!data.content.interviewLanguage,
        },
        // {
        //     label: "Set a Custom Introduction",
        //     completed: !!data.content.customGreeting,
        // },
        // {
        //     label: "Set your Idle Timeout Message",
        //     completed: !!data.content.idleTimeoutMessage,
        // },
    ];

    const completedCount = checklistItems.filter((item) => item.completed).length;
    const totalCount = checklistItems.length;
    const progressPercentage = Math.round((completedCount / totalCount) * 100);

    // Validate if all required fields are filled
    const isValid =
        !!(data.content.description && data.content.description.trim()) &&
        !!data.content.voice &&
        !!data.content.interviewLanguage;

    // Notify parent of validation status
    if (onValidationChange) {
        onValidationChange(isValid);
    }

    const handleFieldChange = (
        field: string,
        value: string
    ) => {
        onDataChange({
            ...data,
            content: {
                ...data.content,
                [field]: value,
            },
        });
    };

    return (
        <div
            className="pb-8 border-b border-gray-200"
            style={{ paddingTop: "24px", paddingBottom: "40px" }}
        >
            {/* Two-Panel Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Left Panel: Header and Description */}
                <div className="lg:col-span-3 flex flex-col space-y-4">
                    <div className="space-y-3">
                        <div className="flex items-center justify-between gap-3 pr-[16px]">
                            <div
                                className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30"
                                style={{ backgroundColor: "#dbeafe", color: "#2563eb" }}
                            >
                                <Settings
                                    className="w-5 h-5 text-blue-600 dark:text-blue-400"
                                    style={{ color: "#2563eb" }}
                                />
                            </div>
                            <div className="flex-1">
                                <div className="flex items-start justify-between gap-2">
                                    <h2 className="text-lg font-semibold">Interview Settings</h2>
                                    <span
                                        className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded"
                                        style={{
                                            paddingTop: "4px",
                                            paddingBottom: "4px",
                                            borderRadius: "1rem",
                                        }}
                                    >
                                        Step 1
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                        Control how your AI interviewer behaves during the session.
                        <br />
                        <br />
                        Adjust the voice, language, and key interaction settings to match
                        your interview style.
                    </p>

                    {/* Spacer to push progress checklist to bottom */}
                    <div className="flex-1" />

                    {/* Progress Checklist - Moved to bottom */}
                    <div className="mt-auto space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                Complete your Interview Settings
                            </h3>
                            <span className="text-xs text-blue-600 dark:text-blue-400">
                                {completedCount}/{totalCount} completed
                            </span>
                        </div>

                        {/* Progress Bar */}
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                            <div
                                className="bg-blue-600 dark:bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${progressPercentage}%` }}
                            />
                        </div>

                        {/* Checklist Items */}
                        <div className="space-y-2.5 pt-1">
                            {checklistItems.map((item, index) => (
                                <div key={index} className="flex items-start gap-2.5">
                                    <div className="mt-0.5">
                                        {item.completed ? (
                                            <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                                        ) : (
                                            <Circle className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                                        )}
                                    </div>
                                    <span
                                        className={`text-sm leading-tight ${item.completed ? "text-gray-700 dark:text-gray-300" : "text-gray-500 dark:text-gray-400"}`}
                                    >
                                        {item.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Panel: Input Fields */}
                <div className="lg:col-span-9 space-y-6">
                    {/* Description Field */}
                    <div className="space-y-2">
                        <Label className="text-base font-medium flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-600" />
                            <div>
                                Interview Description
                                <span className="text-red-500">*</span>
                            </div>
                        </Label>

                        {mode === "edit" ? (
                            <>
                                <p className="text-xs text-gray-500">
                                    This description will be shown to candidates in the interview waiting room prior to starting their session.
                                </p>
                                <div className="relative">
                                    <Textarea
                                        value={data.content.description || ""}
                                        onChange={(e) =>
                                            handleFieldChange("description", e.target.value.slice(0, 200))
                                        }
                                        maxLength={200}
                                        placeholder="Enter interview description..."
                                        className="min-h-[100px] resize-y pb-6"
                                    />
                                    <span className="absolute right-3 bottom-2 text-xs text-gray-400">{(data.content.description || "").length}/200 characters</span>
                                </div>
                            </>
                        ) : (
                            <div className="text-sm py-2 px-3 bg-gray-50 border border-gray-200 rounded-md min-h-[100px]">
                                <span
                                    className={
                                        data.content.description ? "text-gray-900" : "text-gray-500"
                                    }
                                    style={!data.content.description ? { color: "#9ca3af" } : undefined}
                                >
                                    {renderTextWithLineBreaks(data.content.description) || "No description provided."}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Horizontal Row: AI Interview Voice, Interview Language */}
                    <div className={cn("grid grid-cols-1 md:grid-cols-2 gap-6", {
                        "md:grid-cols-2": !["admin", "super_admin"].includes(currentUserInfo?.role || ''),
                        "md:grid-cols-3": ["admin", "super_admin"].includes(currentUserInfo?.role || ''),
                    })}>
                        {/* 1. AI Interview Voice */}
                        <div className="space-y-2">
                            <Label className="text-base font-medium flex items-center gap-2">
                                <Mic className="w-4 h-4 text-blue-600" />
                                <div>
                                    AI Interview Voice
                                    <span className="text-red-500">*</span>
                                </div>
                            </Label>
                            {mode === "edit" && (
                                <p className="text-xs text-gray-500">
                                    Pick the voice that represents your AI interviewer.
                                </p>
                            )}
                            <div>
                                {mode === "edit" ? (
                                    <RadioGroup
                                        value={data.content.voice}
                                        onValueChange={(value: "male" | "female") =>
                                            handleFieldChange("voice", value)
                                        }
                                        className="flex gap-6 p-4 bg-white border border-gray-200 rounded-md h-12 items-center"
                                    >
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="male" id="voice-male" />

                                            <Label htmlFor="voice-male" className="text-sm">
                                                Male
                                            </Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="female" id="voice-female" />

                                            <Label htmlFor="voice-female" className="text-sm">
                                                Female
                                            </Label>
                                        </div>
                                    </RadioGroup>
                                ) : (
                                    <div className="text-sm text-gray-900 p-4 bg-gray-50 border border-gray-200 rounded-md capitalize">
                                        {data.content.voice || "male"}
                                    </div>
                                )}
                            </div>
                        </div>

                        {["admin", "super_admin"].includes(currentUserInfo?.role || '') && (
                            <div className="space-y-2">
                                <Label className="text-base font-medium flex items-center gap-2">
                                    <Mic className="w-4 h-4 text-blue-600" />
                                    <div>
                                        Interview Type
                                    </div>
                                </Label>
                                {mode === "edit" && (
                                    <p className="text-xs text-gray-500">
                                        Choose whether this is an official or practice interview.
                                    </p>
                                )}
                                <div>
                                    {mode === "edit" ? (
                                        <RadioGroup
                                            value={data.content.type}
                                            className="flex gap-6 p-4 bg-white border border-gray-200 rounded-md h-12 items-center"
                                            onValueChange={(value: "official" | "practice") =>
                                                handleFieldChange("type", value)
                                            }
                                        >
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="official" id="type-official" />
                                                <Label htmlFor="type-official" className="text-sm">
                                                    Official
                                                </Label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="practice" id="type-practice" />
                                                <Label htmlFor="type-practice" className="text-sm">
                                                    Practice
                                                </Label>
                                            </div>
                                        </RadioGroup>
                                    ) : (
                                        <div className="text-sm text-gray-900 p-4 bg-gray-50 border border-gray-200 rounded-md capitalize">
                                            {data.content.type || "official"}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {/* 2. Interview Language */}
                        <div className="space-y-2">
                            <Label className="text-base font-medium flex items-center gap-2">
                                <Globe className="w-4 h-4 text-blue-600" />
                                <div>
                                    Interview Language
                                    <span className="text-red-500">*</span>
                                </div>
                            </Label>
                            {mode === "edit" && (
                                <p className="text-xs text-gray-500">
                                    Select the language the AI will use to conduct the interview.
                                </p>
                            )}
                            <div>
                                {mode === "edit" ? (
                                    <>
                                        <div className="flex flex-row items-center align-middle justify-start gap-2">
                                            <Select
                                                value={LANGUAGES.find(lang => lang.value === data.content.interviewLanguage)?.value || ""}
                                                onValueChange={(value) =>
                                                    handleFieldChange("interviewLanguage", value)
                                                }
                                            >
                                                <SelectTrigger className="p-4 bg-white border border-gray-200 rounded-md h-12">
                                                    <SelectValue placeholder={data.content.interviewLanguage} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {LANGUAGES.map((option, index) => (
                                                        <SelectItem key={index} value={option.value}>
                                                            <div className="flex items-center justify-between w-full gap-2">
                                                                <span>{option.label}</span>
                                                                <span
                                                                    role="button"
                                                                    className="p-1 rounded hover:bg-blue-100 transition-colors cursor-pointer"
                                                                    tabIndex={0}
                                                                    onPointerDown={e => { e.preventDefault(); e.stopPropagation(); }}
                                                                    onPointerUp={e => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        const voiceType = (data.content?.voice || "male") as "male" | "female";
                                                                        const fileAudio = option.voice?.[voiceType] || "";
                                                                        if (fileAudio) {
                                                                            setPlayingIndex(index);
                                                                            const audio = new Audio(fileAudio);
                                                                            audio.onended = () => {
                                                                                setPlayingIndex(null);
                                                                            };
                                                                            audio.onerror = () => {
                                                                                setPlayingIndex(null);
                                                                            };
                                                                            audio.oncanplaythrough = () => {
                                                                                audio.play();
                                                                            };
                                                                        }
                                                                    }}
                                                                    onClick={e => { e.preventDefault(); e.stopPropagation(); }}
                                                                >
                                                                    {playingIndex === index ? (
                                                                        <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                                                                    ) : (
                                                                        <Volume2Icon className="w-4 h-4 text-blue-600" />
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-sm text-gray-900 p-4 bg-gray-50 border border-gray-200 rounded-md">
                                        {LANGUAGES.find(lang => lang.value === data.content.interviewLanguage)?.label || "English"}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Custom Introduction */}
                    <div className="space-y-2">
                        <Label className="text-base font-medium flex items-center gap-2">
                            <Wave className="w-4 h-4 text-blue-600" />
                            Custom Introduction
                            <Tooltip content={(
                                <p className="max-w-md">
                                    If left blank, the AI interviewer will use the default
                                    introduction: "Welcome to your quick demo interview! It's
                                    great to meet you. Happy to get to know more about you
                                    today."
                                </p>
                            )}>
                                <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                            </Tooltip>
                        </Label>
                        {mode === "edit" ? (
                            <>
                                <p className="text-xs text-gray-500">
                                    Add a custom greeting that introduces the interview. Candidates
                                    hear this first, so include a simple opening question such as
                                    “Can you tell me about yourself?” to begin the conversation.
                                </p>
                                <Textarea
                                    value={data.content.customGreeting}
                                    onChange={(e) =>
                                        handleFieldChange("customGreeting", e.target.value)
                                    }
                                    placeholder="Welcome to your quick demo interview! It's great to meet you. Happy to get to know more about you today."
                                    className="min-h-[80px] resize-y"
                                />
                            </>
                        ) : (
                            <div className="text-sm py-2 px-3 bg-gray-50 border border-gray-200 rounded-md min-h-[80px]">
                                <span
                                    className={
                                        data.content.customGreeting ? "text-gray-900" : "text-gray-500"
                                    }
                                    style={
                                        !data.content.customGreeting ? { color: "#9ca3af" } : undefined
                                    }
                                >
                                    {renderTextWithLineBreaks(data.content.customGreeting) || "Welcome to your quick demo interview! It's great to meet you. Happy to get to know more about you today."}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Phone-call interview greeting (#3166) — only the outbound
                        WhatsApp phone call uses this; the web interview keeps the
                        Custom Introduction above. Missing enabled ⇒ treat as ON. */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between gap-4">
                            <Label className="text-base font-medium flex items-center gap-2">
                                <Phone className="w-4 h-4 text-blue-600" />
                                Custom greeting for phone-call interviews
                                <Tooltip content={(
                                    <p className="max-w-md">
                                        For AI interviews run as an outbound WhatsApp phone call. Turn
                                        this off to have the agent use its own default opener on the
                                        call. It doesn't affect the web interview.
                                    </p>
                                )}>
                                    <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                                </Tooltip>
                            </Label>
                            {mode === "edit" ? (
                                <Switch
                                    checked={data.content.phoneCallGreetingEnabled ?? true}
                                    onCheckedChange={(checked) =>
                                        onDataChange({ ...data, content: { ...data.content, phoneCallGreetingEnabled: checked } })
                                    }
                                    className="data-[state=checked]:bg-blue-600 flex-shrink-0"
                                />
                            ) : (
                                <span className="text-sm text-gray-600">{(data.content.phoneCallGreetingEnabled ?? true) ? "On" : "Off"}</span>
                            )}
                        </div>
                        {(data.content.phoneCallGreetingEnabled ?? true) && (
                            mode === "edit" ? (
                                <>
                                    <p className="text-xs text-gray-500">
                                        Leave empty to keep the auto-generated personalized greeting.
                                        Use <code className="text-gray-700">{"{name}"}</code>,{" "}
                                        <code className="text-gray-700">{"{role}"}</code> and{" "}
                                        <code className="text-gray-700">{"{company}"}</code> — they're
                                        filled in per candidate.
                                    </p>
                                    <Textarea
                                        value={data.content.phoneCallGreeting || ""}
                                        onChange={(e) => handleFieldChange("phoneCallGreeting", e.target.value)}
                                        placeholder="Hi {name}! This is Maya from ourteam calling about the {role} role at {company}. Is now a good time for a quick chat?"
                                        className="min-h-[80px] resize-y"
                                    />
                                </>
                            ) : (
                                <div className="text-sm py-2 px-3 bg-gray-50 border border-gray-200 rounded-md min-h-[80px]">
                                    <span
                                        className={data.content.phoneCallGreeting ? "text-gray-900" : "text-gray-500"}
                                        style={!data.content.phoneCallGreeting ? { color: "#9ca3af" } : undefined}
                                    >
                                        {renderTextWithLineBreaks(data.content.phoneCallGreeting || "") || "Auto-generated personalized greeting."}
                                    </span>
                                </div>
                            )
                        )}
                    </div>

                    {/* Idle Timeout Message */}
                    <div className="space-y-2">
                        <Label className="text-base font-medium flex items-center gap-2">
                            <Clock className="w-4 h-4 text-blue-600" />
                            Idle Timeout Message
                            <Tooltip content={(
                                <p className="max-w-md">
                                    If left blank, the AI interviewer will use the default
                                    idle message: "It seems like you're busy right now. Have a
                                    nice day!"
                                </p>
                            )}>
                                <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                            </Tooltip>
                        </Label>
                        {mode === "edit" && (
                            <p className="text-xs text-gray-500">
                                This message will appear when the candidate is idle or
                                unresponsive.
                            </p>
                        )}
                        {mode === "edit" ? (
                            <Textarea
                                value={data.content.idleTimeoutMessage}
                                onChange={(e) =>
                                    handleFieldChange("idleTimeoutMessage", e.target.value)
                                }
                                placeholder="It seems like you're busy right now. Have a nice day!"
                                className="min-h-[80px] resize-y"
                            />
                        ) : (
                            <div className="text-sm py-2 px-3 bg-gray-50 border border-gray-200 rounded-md min-h-[80px]">
                                <span
                                    className={
                                        data.content.idleTimeoutMessage ? "text-gray-900" : "text-gray-500"
                                    }
                                    style={
                                        !data.content.idleTimeoutMessage ? { color: "#9ca3af" } : undefined
                                    }
                                >
                                    {renderTextWithLineBreaks(data.content.idleTimeoutMessage) || "It seems like you're busy right now. Have a nice day!"}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Closing Statement */}
                    <div className="space-y-2">
                        <Label className="text-base font-medium flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-blue-600" />
                            Closing Statement
                            <Tooltip content={(
                                <p className="max-w-md">
                                    The AI will ask if the candidate has anything to add, respond briefly if needed, then end the interview. This closing flow is fixed and cannot be edited.
                                </p>
                            )}>
                                <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                            </Tooltip>
                        </Label>
                        {mode === "edit" && (
                            <p className="text-xs text-gray-500">
                                Add the final message your AI interviewer will say after all
                                interview sections are completed. This helps wrap up the
                                interview in a clear and professional way.
                            </p>
                        )}
                        <div className="text-sm py-2 px-3 bg-gray-50 border border-gray-200 rounded-md min-h-[80px]">
                            <span
                                className={
                                    data.content.closingStatement ? "text-gray-900" : "text-gray-500"
                                }
                                style={
                                    !data.content.closingStatement ? { color: "#9ca3af" } : undefined
                                }
                            >
                                Thank you for your time and thoughtful responses. The hiring team will carefully review our conversation, and they will be in touch with you regarding the next steps in the process. Have a great day!
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
